import {FC, useState} from 'react'
import classNames from 'classnames'
import {FormattedMessage} from 'react-intl'

import styles from './EmployerOffers.module.scss'
import EmployerRequestsLayout from 'Layouts/EmployerRequestsLayout'
import OfferListItem from 'Components/EmployerDashboard/OfferListItem'
import {OfferDisplayType} from 'Components/EmployerDashboard/OfferListItem/OfferListItem'
import {EmployerDashboardTranslations as edt} from 'Services/I18n/Constants'
import {NewOfferRequestStatuses} from 'Components/IncomingRequests/Constants'
import InfiniteScroll from 'Components/InfiniteScroll'
import {useMatchingRoute} from 'Services/I18n/Hooks/useMatchingRoute'
import {
  EmployerRequestsSearchParamsType,
  EmployerRequestsStateType,
} from 'Interfaces'
import {ArrowDropDown} from '@mui/icons-material'
import Loader from 'Components/Fallbacks/Loader'
import {NoLongerActiveCandidateModal} from 'Components/Modal/NoLongerActiveCandidateModal'

const getRouteTab = (routeKey: string | undefined): string => {
  switch (routeKey) {
    case 'routes.employeroffersPending':
      return 'pending'
    case 'routes.employeroffersAccepted':
      return 'accepted'
    default:
      return 'all'
  }
}

type EmployerOffersProps = {
  requests: EmployerRequestsStateType
  loadMore: (url: string) => void
  getEmployerOffers: (params?: EmployerRequestsSearchParamsType) => void
  getSeekersFromOffers: (activeTab?: string) => void
  totalOfferRequests?: number
}

export const EmployerOffers: FC<EmployerOffersProps> = ({
  requests,
  getEmployerOffers,
  loadMore,
  getSeekersFromOffers,
  totalOfferRequests,
}) => {
  const matchingRoute = useMatchingRoute()
  const routeTab = getRouteTab(matchingRoute.routeKey)
  const [order, setOrder] = useState('DESC')
  const [sort, setSort] = useState('status')
  const [isModalOpen, setIsModalOpen] = useState(false)

  const onSort = (value: string) => {
    if (sort === value) {
      setOrder((old) => (old === 'ASC' ? 'DESC' : 'ASC'))
    } else {
      setSort(value)
      setOrder('ASC')
    }
    getEmployerOffers({...requests.params, sort, order})
  }

  let itemDisplayType: OfferDisplayType = 'all'
  if (routeTab === 'pending') itemDisplayType = 'pending'
  if (routeTab === 'accepted') itemDisplayType = 'accepted'

  const HeaderTitle = ({value, title}: {value: string; title: string}) => {
    const className = classNames(styles.sortBtn, {
      [styles.active]: value === sort,
      [styles.desc]: order === 'DESC',
    })

    return (
      <div onClick={() => onSort(value)} className={className}>
        <FormattedMessage id={title} />
        <ArrowDropDown />
      </div>
    )
  }

  return (
    <div className={styles.wrap}>
      <EmployerRequestsLayout
        onSearch={(params) => {
          let statuses: string[] | undefined = []
          if (routeTab !== 'all') {
            statuses = params.statuses || requests?.params?.statuses
          }
          getEmployerOffers({...requests.params, statuses, ...params})
        }}
        getSeekers={getSeekersFromOffers}
        tabs={['all', 'pending', 'accepted']}
        activeTab={routeTab}
        type="offers"
        statusOptions={Object.values(NewOfferRequestStatuses)}
        candidatesOptions={requests.candidatesOptions}
      >
        <div
          className={classNames(styles.contentHeader, {
            [styles.all]: itemDisplayType === 'all',
            [styles.accepted]: itemDisplayType === 'accepted',
          })}
        >
          <HeaderTitle value="seekerName" title={edt.candidate} />
          {routeTab !== 'accepted' && (
            <HeaderTitle value="status" title={edt.status} />
          )}

          {routeTab !== 'accepted' && (
            <HeaderTitle value="offerSent" title={edt.offerSent} />
          )}
          {routeTab === 'accepted' && (
            <HeaderTitle value="offer_accepted" title={edt.offerAccepted} />
          )}

          <HeaderTitle value="expiration" title={edt.expire} />

          <div className={styles.leftMargin}>
            <FormattedMessage id={edt.action} />
          </div>
        </div>

        <InfiniteScroll
          loading={requests.loadMoreProgress}
          hasMore={
            totalOfferRequests &&
            totalOfferRequests > requests.searchResults.length
              ? true
              : false
          }
          loadMore={() => loadMore(requests.loadMore as string)}
        >
          <div className={styles.allOffersItems}>
            {requests.loading ? (
              <div className={styles.loaderContainer}>
                <Loader />
                <span className={styles.loadingText}>
                  <FormattedMessage id={edt.loadingText} />
                </span>
              </div>
            ) : requests.error ? (
              <div>{requests.error.message || 'Error'}</div>
            ) : requests.searchResults.length > 0 ? (
              requests.searchResults.map((request, idx) => (
                <OfferListItem
                  request={request}
                  key={`offer-${idx}`}
                  displayType={itemDisplayType}
                  activeTab={routeTab}
                  setIsModalOpen={setIsModalOpen}
                />
              ))
            ) : (
              <div className={styles.noResults}>
                <FormattedMessage id={edt.noResults} />
              </div>
            )}
          </div>
        </InfiniteScroll>
      </EmployerRequestsLayout>
      <NoLongerActiveCandidateModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
      />
    </div>
  )
}
